import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  useGetControlSettingsQuery,
  useUpdateControlSettingsMutation,
} from "../../store/apis/controlApi";
import { ControlSettingsDto } from "../../types/control";

interface ControlSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

type NumericField = Exclude<keyof ControlSettingsDto, "engineEnabled">;

interface FieldDef {
  key: NumericField;
  kind: "percent" | "days";
}

// Las etiquetas y ayudas viven en i18n: control.settingsModal.fields.<key> / <key>Help
const FIELD_DEFS: FieldDef[] = [
  { key: "minDeviationPercent", kind: "percent" },
  { key: "maxAdjustmentPercent", kind: "percent" },
  { key: "investmentOverpacingPercent", kind: "percent" },
  { key: "minElapsedDays", kind: "days" },
  { key: "minRemainingDays", kind: "days" },
  { key: "cooldownDays", kind: "days" },
];

const LabelWithHelp: React.FC<{ label: string; help: string }> = ({
  label,
  help,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    {label}
    <Tooltip title={help}>
      <HelpOutlineIcon sx={{ fontSize: 16 }} />
    </Tooltip>
  </Box>
);

const ControlSettingsModal: React.FC<ControlSettingsModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data: settingsResp, isFetching } = useGetControlSettingsQuery(
    undefined,
    { skip: !open }
  );
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateControlSettingsMutation();

  const loaded = settingsResp?.data;
  const [form, setForm] = useState<ControlSettingsDto | null>(null);

  // Precargar el formulario cuando llegan los settings o se reabre el modal.
  useEffect(() => {
    if (loaded) setForm({ ...loaded });
  }, [loaded, open]);

  const errors = useMemo(() => {
    const e: Partial<Record<NumericField, string>> = {};
    if (!form) return e;
    for (const def of FIELD_DEFS) {
      const v = form[def.key];
      if (v == null || Number.isNaN(v)) {
        e[def.key] = t("control.settingsModal.required");
      } else if (def.kind === "percent") {
        if (v <= 0 || v > 100)
          e[def.key] = t("control.settingsModal.percentError");
      } else {
        if (!Number.isInteger(v) || v < 0)
          e[def.key] = t("control.settingsModal.daysError");
      }
    }
    return e;
  }, [form, t]);

  const hasErrors = Object.keys(errors).length > 0;

  const handleNumber = (key: NumericField, raw: string) => {
    setForm((prev) =>
      prev ? { ...prev, [key]: raw === "" ? NaN : Number(raw) } : prev
    );
  };

  const handleSave = async () => {
    if (!form || !loaded || hasErrors) return;
    // PUT parcial: solo los campos que cambien respecto a lo cargado.
    const diff: Partial<ControlSettingsDto> = {};
    (Object.keys(form) as (keyof ControlSettingsDto)[]).forEach((k) => {
      if (form[k] !== loaded[k]) {
        // @ts-expect-error índice dinámico sobre union de tipos compatibles
        diff[k] = form[k];
      }
    });
    if (Object.keys(diff).length === 0) {
      onClose();
      return;
    }
    try {
      await updateSettings(diff).unwrap();
      dispatch(
        setToastMessage({
          message: t("control.settingsModal.saved"),
          severity: "success",
        })
      );
      onClose();
    } catch (err) {
      const e = err as { data?: { message?: string } };
      dispatch(
        setToastMessage({
          message: e?.data?.message || t("control.settingsModal.saveError"),
          severity: "error",
        })
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5">{t("control.settingsModal.title")}</Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        {isFetching || !form ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.engineEnabled}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev
                        ? { ...prev, engineEnabled: e.target.checked }
                        : prev
                    )
                  }
                />
              }
              label={
                <LabelWithHelp
                  label={t("control.settingsModal.engineEnabled")}
                  help={t("control.settingsModal.engineEnabledHelp")}
                />
              }
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              {FIELD_DEFS.map((def) => {
                const v = form[def.key];
                const label = t(`control.settingsModal.fields.${def.key}`);
                const help = t(`control.settingsModal.fields.${def.key}Help`);
                return (
                  <Grid item xs={12} sm={6} key={def.key}>
                    <TextField
                      type="number"
                      fullWidth
                      size="small"
                      label={<LabelWithHelp label={label} help={help} />}
                      value={Number.isNaN(v) ? "" : v}
                      onChange={(e) => handleNumber(def.key, e.target.value)}
                      error={Boolean(errors[def.key])}
                      helperText={errors[def.key] || help}
                      InputProps={
                        def.kind === "percent"
                          ? {
                              endAdornment: (
                                <InputAdornment position="end">
                                  {t("control.settingsModal.percentUnit")}
                                </InputAdornment>
                              ),
                            }
                          : {
                              endAdornment: (
                                <InputAdornment position="end">
                                  {t("control.settingsModal.daysUnit")}
                                </InputAdornment>
                              ),
                            }
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          disabled={isSaving}
          sx={{ color: (theme) => theme.palette.neutral[600] }}
        >
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || isFetching || !form || hasErrors}
          startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ControlSettingsModal;
